/**
 * ColumnTimelineRow — `column.timeline` renderer. Typed view over a
 * timeline row's config attribute (`linkedCalendarRowId`).
 *
 * Timeline is a **layout row** (`role: "layout", layout:
 * "timeline[row]"`), not a source. It doesn't auto-hydrate a
 * default renderer child — this one is installed via the
 * `TIMELINE.defaultAttributes.childSpecs` entry as a hidden child
 * (`visible: false`). Consumers look it up with
 * `sheetRow.as(ColumnTimelineRow)` or
 * `row.defaultRenderer(ColumnTimelineRow)` (the latter works on any
 * `ModelRow` post-Commit-9m.1 which moved `defaultRenderer` from
 * `SourceRow` to `ModelRow` base).
 */

import type { Option } from "../../core/option.ts";
import { Json, type JsonObject } from "../../core/json.ts";
import { RendererRow } from "./renderer-row.ts";
import { ModelRow } from "../model-row.ts";
import { RowNetworkKey } from "../../ids/row-network-key.ts";

/**
 * The `column.timeline` renderer row: a typed view of a timeline row's `linkedCalendarRowId`
 * attribute.
 */
export class ColumnTimelineRow extends RendererRow {
  /**
   * The timeline's parent row (layout row itself). Unlike
   * renderer-over-source subclasses, timeline's renderer is a
   * sibling read surface — the parent is the layout, not a
   * source, so we surface it as a bare `ModelRow` rather than
   * narrowing to `SourceRow`.
   */
  get parentRow(): Option<ModelRow> {
    return this.parent;
  }

  /**
   * Calendar row this timeline's all-day events are linked to.
   * Stored on the parent layout row's attribute bag — calling
   * into the parent because the layout row is where the plugin
   * persists its config (attribute storage on a renderer child
   * isn't round-tripped through serialize, per
   * `isSyntheticDefaultRendererId`).
   */
  get linkedCalendarRowId(): Option<RowNetworkKey> {
    return this.parent?.attributesAs(ColumnTimelineRow.decodeAttrs)
      ?.linkedCalendarRowId ?? null;
  }

  /**
   * Whether to keep the "+N more" ghost row within the visible
   * rows window. Defaults to `true`.
   */
  get keepGhostWithinVisibleRows(): boolean {
    return (
      this.parent?.attributesAs(ColumnTimelineRow.decodeAttrs)
        ?.keepGhostWithinVisibleRows ?? true
    );
  }
}

/** Types and values that go with `ColumnTimelineRow`: `Attrs`, `decodeAttrs`. */
export namespace ColumnTimelineRow {
  export type Attrs = Partial<{
    linkedCalendarRowId: RowNetworkKey;
    keepGhostWithinVisibleRows: boolean;
  }>;

  /** Read the timeline layout keys off the layout row's bag. */
  export function decodeAttrs(attributes: JsonObject): Attrs {
    const linkedCalendarRowId = Json.string(attributes, "linkedCalendarRowId");
    const keepGhost = Json.boolean(attributes, "keepGhostWithinVisibleRows");
    return {
      ...(linkedCalendarRowId != null ? { linkedCalendarRowId: RowNetworkKey.fromWire(linkedCalendarRowId) } : {}),
      ...(keepGhost != null ? { keepGhostWithinVisibleRows: keepGhost } : {}),
    };
  }
}
